<script setup>
import { computed } from 'vue';
import ReportMetricCard from './ReportMetricCard.vue';

const props = defineProps({
  metrics: {
    type: Object,
    required: true,
  },
  formatCurrency: {
    type: Function,
    required: true,
  },
});

const totalItems = computed(() =>
  (props.metrics.totalItems || 0).toLocaleString()
);
const totalValue = computed(() =>
  props.formatCurrency(props.metrics.totalValue || 0)
);
const totalContacts = computed(() =>
  (props.metrics.contactMetrics?.totalContacts || 0).toLocaleString()
);
const totalConversations = computed(() =>
  (props.metrics.activityMetrics?.itemsWithConversations || 0).toLocaleString()
);
const avgValue = computed(() =>
  props.formatCurrency(props.metrics.averageValue || 0)
);
const itemsCreatedToday = computed(() =>
  (props.metrics.stageMetrics?.itemsCreatedToday || 0).toLocaleString()
);
const itemsCreatedThisWeek = computed(() =>
  (props.metrics.stageMetrics?.itemsCreatedThisWeek || 0).toLocaleString()
);
const itemsCreatedThisMonth = computed(() =>
  (props.metrics.stageMetrics?.itemsCreatedThisMonth || 0).toLocaleString()
);
const totalOffers = computed(() =>
  (props.metrics.stageMetrics?.totalOffers || 0).toLocaleString()
);
const avgOffersValue = computed(() =>
  props.formatCurrency(props.metrics.stageMetrics?.avgOffersValue || 0)
);
const itemsWithChecklists = computed(() =>
  (props.metrics.checklistMetrics?.itemsWithChecklists || 0).toLocaleString()
);
const checklistCompletionRate = computed(
  () =>
    `${((props.metrics.checklistMetrics?.completionRate || 0) * 100).toFixed(1)}%`
);
const itemsWithDeadline = computed(() =>
  (props.metrics.stageMetrics?.itemsWithDeadline || 0).toLocaleString()
);
const itemsWithAttachments = computed(() =>
  (props.metrics.activityMetrics?.itemsWithAttachments || 0).toLocaleString()
);
const itemsWithNotes = computed(() =>
  (props.metrics.activityMetrics?.itemsWithNotes || 0).toLocaleString()
);
const itemsWithRescheduling = computed(() =>
  (props.metrics.stageMetrics?.itemsWithRescheduling || 0).toLocaleString()
);
const itemsWithOffers = computed(() =>
  (props.metrics.stageMetrics?.itemsWithOffers || 0).toLocaleString()
);
const avgActivitiesPerItem = computed(() =>
  (
    props.metrics.activityMetrics?.averageActivitiesPerItem || 0
  ).toLocaleString()
);

// SiteUP CRM (relatorios r1, item 3b): bloco "Fechados no periodo". Ganhos,
// perdas, receita, ticket medio, conversao e ciclo saem da DATA DE FECHAMENTO
// (won_at/lost_at), nao de created_at. Os cartoes acima continuam sendo o
// recorte por criacao, e cada bloco diz qual e o seu.
const closed = computed(() => props.metrics.closedInPeriod || null);
const days = value =>
  value === null || value === undefined ? '·' : `${Number(value).toFixed(1)}`;
</script>

<template>
  <div class="flex flex-col gap-4">
    <h3 class="text-sm font-semibold text-n-slate-12">
      {{ $t('REPORTS.CLOSED_PERIOD.CREATED_BLOCK') }}
    </h3>
    <div
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mx-0 outline outline-1 outline-n-weak rounded-2xl bg-n-solid-1 px-4 py-4"
    >
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.TOTAL_ITEMS')"
        :value="totalItems"
        :info-text="$t('REPORTS.KANBAN_METRICS.TOTAL_ITEMS_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.TOTAL_VALUE')"
        :value="totalValue"
        :info-text="$t('REPORTS.KANBAN_METRICS.TOTAL_VALUE_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.AVERAGE_VALUE')"
        :value="avgValue"
        :info-text="$t('REPORTS.KANBAN_METRICS.AVERAGE_VALUE_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_TODAY')"
        :value="itemsCreatedToday"
        :info-text="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_TODAY_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_THIS_WEEK')"
        :value="itemsCreatedThisWeek"
        :info-text="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_THIS_WEEK_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_THIS_MONTH')"
        :value="itemsCreatedThisMonth"
        :info-text="$t('REPORTS.KANBAN_METRICS.ITEMS_CREATED_THIS_MONTH_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.TOTAL_OFFERS')"
        :value="totalOffers"
        :info-text="$t('REPORTS.KANBAN_METRICS.TOTAL_OFFERS_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.AVERAGE_OFFERS_VALUE')"
        :value="avgOffersValue"
        :info-text="$t('REPORTS.KANBAN_METRICS.AVERAGE_OFFERS_VALUE_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_CHECKLIST')"
        :value="itemsWithChecklists"
        :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_CHECKLIST_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.CHECKLIST_COMPLETION_RATE')"
        :value="checklistCompletionRate"
        :info-text="$t('REPORTS.KANBAN_METRICS.CHECKLIST_COMPLETION_RATE_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_DEADLINE')"
        :value="itemsWithDeadline"
        :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_DEADLINE_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_ATTACHMENTS')"
        :value="itemsWithAttachments"
        :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_ATTACHMENTS_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_NOTES')"
        :value="itemsWithNotes"
        :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_NOTES_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_CONVERSATION')"
        :value="totalConversations"
        :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_CONVERSATION_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_RESCHEDULING')"
        :value="itemsWithRescheduling"
        :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_RESCHEDULING_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_OFFERS')"
        :value="itemsWithOffers"
        :info-text="$t('REPORTS.KANBAN_METRICS.CARDS_WITH_OFFERS_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.AVERAGE_ACTIVITIES_PER_ITEM')"
        :value="avgActivitiesPerItem"
        :info-text="
          $t('REPORTS.KANBAN_METRICS.AVERAGE_ACTIVITIES_PER_ITEM_INFO')
        "
      />
      <ReportMetricCard
        :label="$t('REPORTS.KANBAN_METRICS.TOTAL_CONTACTS')"
        :value="totalContacts"
        :info-text="$t('REPORTS.KANBAN_METRICS.TOTAL_CONTACTS_INFO')"
      />
    </div>

    <h3 v-if="closed" class="text-sm font-semibold text-n-slate-12">
      {{ $t('REPORTS.CLOSED_PERIOD.CLOSED_BLOCK') }}
    </h3>
    <p
      v-if="closed && closed.wonWithoutDate"
      class="flex items-start gap-2 rounded-xl border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-sm text-n-amber-11"
      role="status"
      data-testid="won-without-date-warning"
    >
      <span class="i-ph-warning mt-0.5 size-4 shrink-0" aria-hidden="true" />
      {{
        $t(
          'REPORTS.CLOSED_PERIOD.WON_WITHOUT_DATE',
          { count: closed.wonWithoutDate },
          closed.wonWithoutDate
        )
      }}
    </p>
    <p
      v-if="closed && closed.lostWithoutDate"
      class="flex items-start gap-2 rounded-xl border border-n-amber-6 bg-n-amber-2 px-3 py-2 text-sm text-n-amber-11"
      role="status"
      data-testid="lost-without-date-warning"
    >
      <span class="i-ph-warning mt-0.5 size-4 shrink-0" aria-hidden="true" />
      {{
        $t(
          'REPORTS.CLOSED_PERIOD.LOST_WITHOUT_DATE',
          { count: closed.lostWithoutDate },
          closed.lostWithoutDate
        )
      }}
    </p>
    <!-- SiteUP CRM (relatorios r1, decisao 27): venda recuperada pela etapa
         entra com a data em que o cartao ENTROU na etapa, nao com a data real
         do fechamento. A tela diz isso em vez de apresentar estimativa como
         medicao. -->
    <p
      v-if="closed && closed.wonFromStageBackfill"
      class="text-xs text-n-slate-11"
      data-testid="won-from-stage-backfill-note"
    >
      {{
        $t(
          'REPORTS.CLOSED_PERIOD.WON_FROM_STAGE_BACKFILL',
          { count: closed.wonFromStageBackfill },
          closed.wonFromStageBackfill
        )
      }}
    </p>
    <div
      v-if="closed"
      class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 mx-0 outline outline-1 outline-n-weak rounded-2xl bg-n-solid-1 px-4 py-4"
    >
      <ReportMetricCard
        :label="$t('REPORTS.CLOSED_PERIOD.WON')"
        :value="closed.won.toLocaleString()"
        :info-text="$t('REPORTS.CLOSED_PERIOD.CLOSED_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.CLOSED_PERIOD.LOST')"
        :value="closed.lost.toLocaleString()"
        :info-text="$t('REPORTS.CLOSED_PERIOD.CLOSED_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.CLOSED_PERIOD.REVENUE')"
        :value="formatCurrency(closed.revenue)"
        :info-text="$t('REPORTS.CLOSED_PERIOD.CLOSED_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.CLOSED_PERIOD.AVERAGE_TICKET')"
        :value="formatCurrency(closed.averageTicket)"
        :info-text="$t('REPORTS.CLOSED_PERIOD.CLOSED_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.CLOSED_PERIOD.CONVERSION_RATE')"
        :value="`${closed.conversionRate}%`"
        :info-text="$t('REPORTS.CLOSED_PERIOD.CLOSED_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.CLOSED_PERIOD.SALES_CYCLE_AVG')"
        :value="days(closed.salesCycleAvgDays)"
        :info-text="$t('REPORTS.CLOSED_PERIOD.CLOSED_INFO')"
      />
      <ReportMetricCard
        :label="$t('REPORTS.CLOSED_PERIOD.SALES_CYCLE_MEDIAN')"
        :value="days(closed.salesCycleMedianDays)"
        :info-text="$t('REPORTS.CLOSED_PERIOD.CLOSED_INFO')"
      />
    </div>
  </div>
</template>
